import React from 'react';
import { Message } from 'semantic-ui-react';

import { clienteApi } from 'src/api';
import { Contexto } from 'src/contexto';
import { MenuTabla, TablaDeDeudores } from '.';
import { ModalCorreos } from '../../ModalCorreos';
import {
    Columna,
    FiltroCompromiso,
    FiltroSede,
    FormasDePago,
    ObtenerApoderadosDeudoresResp,
} from '../tipos';

type Datos = ObtenerApoderadosDeudoresResp;
type ApoderadoDeudor = NonNullable<Datos['porId'][string]>;
type DireccionOrden = 'ascending' | 'descending';

interface Props {
    readonly ocultar: boolean;
}

interface State {
    readonly datos: Datos;
    readonly idsFiltrados: ReadonlyArray<string>;
    readonly columnaAOrdenar: Columna;
    readonly direccionDelOrden: DireccionOrden;
    readonly cargando: boolean;
    readonly mostrandoCorreos: boolean;
    readonly diaSeleccionado: number | undefined;
    readonly formaDePagoSeleccionada: FormasDePago | 'todos';
    readonly añoSeleccionado: number | 'todos';
    readonly filtroCompromisoSeleccionado: FiltroCompromiso;
    readonly sedeSeleccionada: FiltroSede;
}

const stateInicial: State = {
    datos: {
        porId: {},
        todosLosId: [],
    },
    idsFiltrados: [],
    columnaAOrdenar: 'nombreCompleto',
    direccionDelOrden: 'ascending',
    cargando: false,
    mostrandoCorreos: false,
    diaSeleccionado: undefined,
    formaDePagoSeleccionada: 'todos',
    añoSeleccionado: 'todos',
    filtroCompromisoSeleccionado: 'sinCompromiso',
    sedeSeleccionada: 'todos',
};

class DeudoresCuotas extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state = stateInicial;

    public componentDidMount() {
        if (!this.props.ocultar) {
            this.traerApoderados();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (!this.props.ocultar && prevProps.ocultar && this.state.datos.todosLosId.length === 0) {
            this.traerApoderados();
        }
    }

    public render() {
        if (this.props.ocultar) return null;

        const {
            datos,
            idsFiltrados,
            cargando,
            mostrandoCorreos,
            diaSeleccionado,
            formaDePagoSeleccionada,
            añoSeleccionado,
            filtroCompromisoSeleccionado,
            sedeSeleccionada,
            columnaAOrdenar,
            direccionDelOrden,
        } = this.state;

        const datosFiltrados = idsFiltrados.map(id => datos.porId[id]!);

        return (
            <div>
                {mostrandoCorreos && this.renderizarModalCorreos(datosFiltrados)}

                <Message warning>
                    Se consideran como deudores aquellos que tengan al menos una cuota atrasada (con
                    o sin abono) desde hace al menos 5 días corridos.
                </Message>

                <MenuTabla
                    cantidadDeApoderados={datosFiltrados.length}
                    cargando={cargando}
                    diaDePago={diaSeleccionado}
                    formaDePago={formaDePagoSeleccionada}
                    año={añoSeleccionado}
                    compromiso={filtroCompromisoSeleccionado}
                    sede={sedeSeleccionada}
                    alPresionarRecargar={this.traerApoderados}
                    alElegirDiaDePago={this.cambiarDiaSeleccionado}
                    alElegirFormaDePago={this.cambiarFormaDePago}
                    alElegirAño={this.cambiarAñoSeleccionado}
                    alElegirCompromiso={this.cambiarFiltroCompromiso}
                    alElegirSede={this.cambiarSedeSeleccionada}
                    alPresionarCorreos={() => this.setState({ mostrandoCorreos: true })}
                />

                <TablaDeDeudores
                    datos={datosFiltrados}
                    columnaAOrdenar={columnaAOrdenar}
                    direccionDelOrden={direccionDelOrden}
                    alClickearCabecera={this.manejarOrdenamiento}
                />
            </div>
        );
    }

    private readonly manejarOrdenamiento = (columnaClickeada: Columna) => {
        const { datos, idsFiltrados, direccionDelOrden } = this.state;
        const columnaAnterior = this.state.columnaAOrdenar;

        let nuevaDireccion: DireccionOrden = 'ascending';

        if (columnaAnterior === columnaClickeada) {
            nuevaDireccion = direccionDelOrden === 'ascending' ? 'descending' : 'ascending';
        }

        this.setState({
            idsFiltrados: ordenar({
                datos,
                idsFiltrados,
                direccionDelOrden: nuevaDireccion,
                ordenarPor: columnaClickeada,
            }),
            columnaAOrdenar: columnaClickeada,
            direccionDelOrden: nuevaDireccion,
        });
    };

    private readonly traerApoderados = (
        opciones: {
            diaDePago?: number | undefined;
            formaDePago?: FormasDePago | 'todos';
            año?: number | 'todos';
            compromiso?: 'conCompromiso' | 'sinCompromiso' | 'todos';
            sede?: FiltroSede;
        } = {},
    ) => {
        const {
            diaDePago = this.state.diaSeleccionado,
            formaDePago = this.state.formaDePagoSeleccionada,
            año = this.state.añoSeleccionado,
            compromiso = this.state.filtroCompromisoSeleccionado,
            sede = this.state.sedeSeleccionada,
        } = opciones;

        this.setState({ cargando: true });
        clienteApi
            .ejecutar('obtenerApoderadosDeudores', {
                diaDePago,
                formaDePago,
                año,
                compromiso,
                sede,
            })
            .then(datos => {
                const idsFiltrados = ordenar({
                    datos,
                    idsFiltrados: datos.todosLosId,
                    direccionDelOrden: this.state.direccionDelOrden,
                    ordenarPor: this.state.columnaAOrdenar,
                });
                this.setState({
                    cargando: false,
                    datos,
                    idsFiltrados,
                });
            })
            .catch(error => {
                this.context.mostrarModalError(error);
                this.setState(stateInicial);
            });
    };

    private renderizarModalCorreos(datosFiltrados: ReadonlyArray<ApoderadoDeudor>) {
        const correos = datosFiltrados.map(d => ({ año: d.paraElAño, correo: d.correo }));

        return (
            <ModalCorreos
                titulo='Correos electrónicos de deudores'
                correos={correos}
                alCerrar={() => this.setState({ mostrandoCorreos: false })}
            />
        );
    }

    private readonly cambiarDiaSeleccionado = (diaDePago: number | undefined) => {
        this.setState({ diaSeleccionado: diaDePago });
        this.traerApoderados({ diaDePago });
    };

    private readonly cambiarFormaDePago = (formaDePago: FormasDePago | 'todos') => {
        if (formaDePago === 'tarjeta' || formaDePago === 'contado' || formaDePago === 'todos') {
            this.setState({ formaDePagoSeleccionada: formaDePago, diaSeleccionado: undefined });
            this.traerApoderados({ diaDePago: undefined, formaDePago });
        } else {
            this.setState({ formaDePagoSeleccionada: formaDePago });
            this.traerApoderados({ formaDePago });
        }
    };

    private readonly cambiarAñoSeleccionado = (año: number | 'todos') => {
        this.setState({ añoSeleccionado: año });
        this.traerApoderados({ año });
    };

    private readonly cambiarFiltroCompromiso = (compromiso: FiltroCompromiso) => {
        this.setState({ filtroCompromisoSeleccionado: compromiso });
        this.traerApoderados({ compromiso });
    };

    private readonly cambiarSedeSeleccionada = (sede: FiltroSede) => {
        this.setState({ sedeSeleccionada: sede });
        this.traerApoderados({ sede });
    };
}

function ordenar(args: {
    ordenarPor: Columna;
    direccionDelOrden: DireccionOrden;
    idsFiltrados: ReadonlyArray<string>;
    datos: ObtenerApoderadosDeudoresResp;
}) {
    const { ordenarPor, direccionDelOrden, idsFiltrados, datos } = args;

    const factorDeOrden = direccionDelOrden === 'ascending' ? 1 : -1;

    // Convierte el arreglo de idsFiltrados en un arreglo de tuplas [id, indice].
    // El índice se usará para estabilizar el ordenamiento.
    const arregloDeTuplas = idsFiltrados.map((x, i) => [x, i] as [string, number]);

    const compararNumeros = (a: number, b: number) => (a - b) * factorDeOrden;
    const compararTextos = (a: string, b: string) => a.localeCompare(b, 'es') * factorDeOrden;

    arregloDeTuplas.sort(([idA, indiceA], [idB, indiceB]) => {
        const a = datos.porId[idA]!;
        const b = datos.porId[idB]!;

        if (
            ordenarPor === 'diasDeAtraso' ||
            ordenarPor === 'montoDeLaDeuda' ||
            ordenarPor === 'numeroDeCuota'
        ) {
            return (
                compararNumeros(
                    a.cuotasAtrasadas[0][ordenarPor],
                    b.cuotasAtrasadas[0][ordenarPor],
                ) || indiceA - indiceB
            );
        }
        if (ordenarPor === 'fechaDeVencimiento') {
            return (
                compararTextos(
                    a.cuotasAtrasadas[0][ordenarPor],
                    b.cuotasAtrasadas[0][ordenarPor],
                ) || indiceA - indiceB
            );
        }
        if (ordenarPor === 'paraElAño') {
            return compararNumeros(a.paraElAño, b.paraElAño) || indiceA - indiceB;
        }
        // si los elementos son iguales, usar el índice anterior (estabilidad)
        return compararTextos(a[ordenarPor], b[ordenarPor]) || indiceA - indiceB;
    });

    return arregloDeTuplas.map(x => x[0]);
}

export default DeudoresCuotas;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/Deudores/componentes/DeudoresCuotas.tsx